<template>
  <div v-if="showBanner" class="consent-container">
    <div class="consent-banner">
      <div
        class="consent-banner-content"
        :class="{ 'consent-banner-content--config': showConfig }"
      >
        <div class="consent-text">
          <template v-if="!showConfig">
            <div class="consent-text-title">
              {{ $t('cookiesBanner.title') }}
            </div>
            {{ $t('cookiesBanner.message') }}
            <nav class="consent-legal-links">
              <a
                v-for="link in legalDocumentLinks"
                :key="link.key"
                :href="getLegalDocumentHref(link)"
                @click="handleLegalDocumentClick(link, $event)"
              >
                {{ $t(link.label) }}
              </a>
            </nav>
          </template>

          <template v-else>
            <div class="consent-text-title margin-bottom-half">
              {{ $t('cookiesBanner.preferences') }}
            </div>
            <div class="cookies-preferences">
              <div
                class="cookies-preference-option cookies-preference-option--fixed"
              >
                <DxSwitch :value="true" :disabled="true" />
                <div class="cookies-preference-copy">
                  <span class="cookies-preference-title">
                    {{ $t('cookiesBanner.technicalCookies') }}
                  </span>
                  <span class="cookies-preference-description">
                    {{
                      $t('cookiesBanner.technicalCookiesDescription')
                    }}
                  </span>
                </div>
                <span class="cookies-preference-badge">
                  {{ $t('cookiesBanner.alwaysActive') }}
                </span>
              </div>

              <label class="cookies-preference-option">
                <DxSwitch
                  :value="enabledGoogleAnalytics"
                  @valueChanged="
                    enabledGoogleAnalytics = $event.value
                  "
                />
                <span class="cookies-preference-copy">
                  <span class="cookies-preference-title">
                    {{ $t('cookiesBanner.analyticsCookies') }}
                  </span>
                  <span class="cookies-preference-description">
                    {{
                      $t('cookiesBanner.analyticsCookiesDescription')
                    }}
                  </span>
                </span>
              </label>
            </div>
          </template>
        </div>

        <div class="consent-buttons">
          <template v-if="!showConfig">
            <button
              type="button"
              class="consent-button consent-button-config"
              @click="showConfig = true"
            >
              {{ $t('cookiesBanner.config') }}
            </button>
            <button
              type="button"
              class="consent-button consent-button-cancel"
              @click="doNotAccept"
            >
              {{ $t('cookiesBanner.resign') }}
            </button>
            <button
              type="button"
              class="consent-button consent-button-accept"
              @click="accept"
            >
              {{ $t('cookiesBanner.accept') }}
            </button>
          </template>

          <template v-else>
            <button
              type="button"
              class="consent-button consent-button-cancel"
              @click="showConfig = false"
            >
              {{ $t('goBack') }}
            </button>
            <button
              type="button"
              class="consent-button consent-button-save"
              @click="savePreferences"
            >
              {{ $t('cookiesBanner.savePreferences') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { DxSwitch } from 'devextreme-vue/switch';
import { getAppHashUrl } from '@/js/appEntryUrl';

export default {
  name: 'ConsentGDPR',
  components: {
    DxSwitch,
  },
  data() {
    return {
      disableStr: 'ga-disable-G-FSF6PYKPGD',
      showBanner: localStorage['consent-gdpr'] !== 'true',
      showConfig: false,
      enabledGoogleAnalytics: true,
      legalDocumentLinks: [
        {
          key: 'privacy',
          label: 'cookiesBanner.privacyPolicy',
          sectionId: 'privacy-policy',
        },
        {
          key: 'terms',
          label: 'cookiesBanner.termsOfUse',
          sectionId: 'terms-of-use',
        },
        {
          key: 'cookies',
          label: 'cookiesBanner.cookiePolicy',
          sectionId: 'cookie-policy',
        },
        {
          key: 'security',
          label: 'cookiesBanner.securityPolicy',
          sectionId: 'information-security-policy',
        },
        {
          key: 'legalNotice',
          label: 'cookiesBanner.legalNotice',
          sectionId: 'legal-notice',
        },
      ],
    };
  },
  computed: {
    ...mapState('user', ['isUserLoggedIn']),
  },
  methods: {
    ...mapActions('user', ['setShowPrivacyPolicy']),
    getLegalDocumentHref(link) {
      if (this.isUserLoggedIn) {
        return `/legal-documents-internal?section=${link.sectionId}`;
      }

      return getAppHashUrl(
        `/legal-documents?section=${link.sectionId}`
      );
    },
    handleLegalDocumentClick(link, event) {
      event.preventDefault();

      if (!this.isUserLoggedIn) {
        // Sin sesion usamos una navegacion real a la page publica. El router
        // principal no existe en este modo y solo cambiar el hash deja el login
        // montado hasta que el usuario recarga manualmente.
        const targetUrl = this.getLegalDocumentHref(link);

        window.location.href = targetUrl;
        window.location.reload();
        return;
      }

      this.$f7ready(() => {
        const router = this.$f7?.views?.main?.router;
        const target = this.getLegalDocumentHref(link);

        if (router) {
          // Con sesion reutilizamos el router principal para mantener navbar y
          // menu simple. reloadCurrent fuerza que cambie la seccion aunque ya
          // estemos en la pantalla interna de documentos.
          router.navigate(target, {
            animate: true,
            transition: 'f7-fade',
            reloadCurrent: true,
          });
          return;
        }

        window.location.assign(getAppHashUrl(target));
      });
    },
    savePreferences() {
      if (this.enabledGoogleAnalytics) {
        this.accept();
        return;
      }

      this.doNotAccept();
    },
    accept() {
      if (!this.enabledGoogleAnalytics) {
        this.doNotAccept();
        return;
      }

      document.cookie = `${this.disableStr}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      window[this.disableStr] = false;
      localStorage['consent-gdpr'] = true;

      this.showBanner = false;

      window.location.reload();
    },
    doNotAccept() {
      document.cookie = `${this.disableStr}=true; expires=Thu, 31 Dec 2099 23:59:59 UTC; path=/`;
      window[this.disableStr] = true;
      localStorage['consent-gdpr'] = true;

      this.showBanner = false;

      window.location.reload();
    },
  },
};
</script>

<style>
.consent-container {
  max-width: 83rem;
  margin-left: auto;
  margin-right: auto;
  padding: 0 2rem;
  position: absolute;
  bottom: 1rem;
  z-index: 99999;
  left: 0;
  right: 0;
}

.consent-banner {
  background: #073f6f;
  color: white;
  padding: 0.95rem 1rem;
  border-radius: 8px;
  box-shadow: 0 10px 26px rgba(0, 31, 63, 0.22);
}

.consent-banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.consent-banner-content--config {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
}

.consent-text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.consent-text-title {
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 2rem;
  align-items: center;
  margin-bottom: 0.5rem;
}

.consent-legal-links {
  width: max-content;
  max-width: 100%;
  margin: 0.75rem auto 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.consent-legal-links a {
  color: rgba(255, 255, 255, 0.92) !important;
  font-size: 0.8rem;
  line-height: 1rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.consent-legal-links a:hover,
.consent-legal-links a:focus-visible {
  color: #fff !important;
}

.cookies-preferences {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-top: 0.35rem;
}

.cookies-preference-option {
  min-width: 0;
  min-height: 3.1rem;
  padding: 0.55rem 0.7rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  background: #fff;
  color: #24364a;
  border: 1px solid rgba(180, 203, 226, 0.85);
  border-radius: 8px;
  font-weight: 500;
}

.cookies-preference-option:not(.cookies-preference-option--fixed) {
  cursor: pointer;
}

.cookies-preference-copy {
  min-width: 0;
  display: grid;
  gap: 0.1rem;
}

.cookies-preference-title {
  font-weight: 700;
}

.cookies-preference-description {
  color: #66758a;
  font-size: 0.78rem;
  line-height: 1.15rem;
}

.cookies-preference-badge {
  justify-self: start;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  background: #edf5fd;
  color: #0b5a9c;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.consent-buttons {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.55rem;
  white-space: nowrap;
}

.consent-banner-content--config .consent-buttons {
  justify-content: flex-end;
}

.consent-button {
  min-height: 2.6rem;
  width: 11rem;
  padding: 0.6rem 1.15rem;
  border: none;
  border-radius: 0.375rem;
  outline: none;
  font-weight: 700;
  cursor: pointer;
}

.consent-button-cancel {
  background: #0b5a9c;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.24);
}

.consent-button-cancel:hover {
  background: #08497f;
}

.consent-button-config {
  background: #46993f;
  color: white;
}

.consent-button-config:hover {
  background: #3c7a33;
}

.consent-button-accept {
  background: #fff;
  color: #073f6f;
}

.consent-button-save {
  background: #fff;
  color: #073f6f;
}

.consent-button-accept:hover,
.consent-button-save:hover {
  background: #e5e7eb;
}

@media (max-width: 800px) {
  .consent-banner-content {
    display: block;
  }

  .consent-text {
    padding-bottom: 0.8rem;
  }

  .consent-buttons {
    flex-wrap: wrap;
  }
}
</style>
